import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
  signal,
  computed,
  inject,
  input,
  viewChild
} from "@angular/core";
import {
  AgentParam,
  AppConfigService,
  ApplicationService,
  ApplicationTypeEnum,
  ConversationDestinationEnum,
  ConversationOriginEnum,
  GenericErrorHandler,
  IInteraction,
  IQueue,
  MediaRulesService,
  NotificationService,
  ProtectedTicketService,
  ScheduleTypeEnum,
  SizeEnum,
  TicketTypeEnum,
  UserService,
  WindowService,
  copyTextToClipboard,
  extractErrorMessage,
  slideInOut
} from "../../../core-ui";
import { ConversationTypeEnum } from "@auvious/integrations";
import { TranslateService } from "@ngx-translate/core";
import { InteractionService } from "../../services/interaction.service";
import { InvitationService } from "../../services/invitation.service";
import { QRService } from "../../services/qr.service";
import { QueueService } from "../../services/queue.service";
import { SmsService } from "../../services/sms.service";
import { PARAM_CONVERSATION_ORIGIN, smsFlow } from "../../app.enums";
import { AnalyticsService } from "../../../core-ui/services/analytics.service";
import { GenesysInteraction, GenesysInvitationChannelEnum } from "../../models";
export interface IShareLinkAction {
  shareLink: () => void;
}

@Component({
  selector: "app-share-link",
  templateUrl: "./share-link.component.html",
  styleUrls: ["./share-link.component.scss"],
  animations: [slideInOut],
  standalone: false
})
export class ShareLinkComponent implements OnInit {
  private config = inject(AppConfigService);
  private notification = inject(NotificationService);
  private translate = inject(TranslateService);
  private user = inject(UserService);
  private applicationService = inject(ApplicationService);
  private invitationService = inject(InvitationService);
  private ticketService = inject(ProtectedTicketService);
  private errorHandler = inject(GenericErrorHandler);
  private qrService = inject(QRService);
  private analyticsService = inject(AnalyticsService);
  private interactionService = inject(InteractionService);
  private winService = inject(WindowService);
  private cd = inject(ChangeDetectorRef);
  private mediaRules = inject(MediaRulesService);
  private queueService = inject(QueueService);
  private smsService = inject(SmsService);

  readonly interaction = input<IInteraction>(undefined);
  readonly isOriginWidgetWithoutVideo = input<boolean>(undefined);
  readonly smsFlow = input<smsFlow>(undefined);

  readonly menuRef = viewChild("menuRef", { read: ElementRef });
  readonly menuButtonRef = viewChild("menuButtonRef", { read: ElementRef });

  shareOpen = signal(false);
  isCallbackRequest = false;
  isAgent: boolean;
  isTestApplication: boolean;
  customerLink = signal<string>(null);
  customerTicket = signal<string>(null);
  agentLink = signal<string>(null);
  isPhoneNumberValidation: boolean;
  isEmailValidation: boolean;
  smsQueue: IQueue;
  emailQueue: IQueue;
  channel: GenesysInvitationChannelEnum;
  workflow: string;
  buttonSize = SizeEnum.small;
  isCopied = signal(false);
  customerPhoneNumber;
  isTicketVisible = signal(false);

  async ngOnInit() {
    this.isAgent = this.user.isAgent;
    this.isTestApplication =
      this.applicationService.getActiveApplication().getType() ===
      ApplicationTypeEnum.Test;

    this.agentLink.set(
      this.invitationService.prepareAgentInvitation(this.interaction())
    );
    this.isTicketVisible.set(
      this.config.agentParamEnabled(
        AgentParam.CUSTOMER_INVITATION_TICKET_VISIBLE
      )
    );
    if (!this.user.isGuestAgent) {
      if (this.smsFlow() === smsFlow.auto) {
        await this.renewCustomerLink();
        this.autoSendSMS();
      } else {
        this.renewCustomerLink();
      }
    }
    if (
      !(
        this.isOriginWidget ||
        this.isOriginCallback ||
        this.isOriginAppointment ||
        this.config.agentParamEnabled(AgentParam.AUTO_INVITE_CUSTOMER)
      )
    ) {
      this.toggleShare();
    }
    // this.subscription.add(
    //   this.winService.streamControlsContainerPositionChange$.subscribe((e) => {
    //     this.buttonSize =
    //       e.left < STREAM_CONTROLS_LEFT_OFFSET_THRESHOLD
    //         ? SizeEnum.extraSmall
    //         : SizeEnum.small;
    //     this.cd.detectChanges();
    //   })
    // );
  }

  @HostListener("document:keydown.escape", ["$event"]) keydown(
    e: KeyboardEvent
  ) {
    if (this.shareOpen()) {
      this.toggleShare();
    }
  }

  autoSendSMS() {
    this.customerPhoneNumber = this.interaction().getCustomerPhoneNumber();
    if (!this.customerPhoneNumber) return;

    const channel =
      this.config.agentParamEnabled(AgentParam.SMS_WORKFLOW_ENABLED) &&
      !!this.config.agentParam(AgentParam.SMS_WORKFLOW_ID)
        ? GenesysInvitationChannelEnum.workflow
        : GenesysInvitationChannelEnum.queue;

    switch (channel) {
      case GenesysInvitationChannelEnum.queue:
        this.sendSMSUsingQueue();
        break;
      case GenesysInvitationChannelEnum.workflow:
        this.sendSMSUsingWorkflow();
        break;
    }
  }

  async sendSMSUsingWorkflow() {
    try {
      await this.smsService.sendUsingWorkflow({
        flowId: this.workflow,
        interaction: this.interaction() as GenesysInteraction,
        phoneNumber: this.customerPhoneNumber,
        link: this.customerLink()
      });
      this.notification.info(this.translate.instant("Workflow executed"));
    } catch (ex) {
      this.notification.error("Could not execute workflow", {
        body: extractErrorMessage(ex)
      });
    }
  }

  async sendSMSUsingQueue() {
    try {
      const interactionValue = this.interaction();
      if (!interactionValue.supportsSMS()) return;

      const queues = await this.queueService.getSMSQueues();
      if (queues.length === 0) return;

      await this.smsService.sendUsingQueue({
        queueId: queues[0].id,
        interaction: interactionValue,
        phoneNumber: this.customerPhoneNumber,
        link: this.customerLink()
      });
      this.notification.info("SMS sent", {
        body: this.translate.instant(
          "Please check your interactions for a new SMS interaction."
        )
      });
    } catch (ex) {
      this.notification.error(
        (ex.body && ex.body.message) || ex.message || "Could not prepare SMS"
      );
    }
  }

  public inviteCustomer() {
    const interaction = this.interaction();
    this.interactionService.invite(
      interaction.getType(),
      interaction,
      this.customerTicket()
    );
    interaction.setCustomerInvited(true);
    this.analyticsService.trackInvitationSent(interaction);
  }

  protected optionsClick(e: Event) {
    e.stopPropagation();
  }

  protected toggleShare() {
    if (this.shareOpen()) {
      this.shareOpen.set(false);
    } else if (
      !this.isCallbackRequest &&
      !this.isPhoneNumberValidation &&
      !this.isEmailValidation
    ) {
      this.shareOpen.set(true);
    }
    if (this.isCallbackRequest) {
      this.isCallbackRequest = false;
    }
    if (this.isPhoneNumberValidation) {
      this.isPhoneNumberValidation = false;
    }
    if (this.isEmailValidation) {
      this.isEmailValidation = false;
    }

    this.cd.detectChanges();

    if (this.shareOpen()) {
      this.winService
        .onClickedOutside(
          this.menuRef().nativeElement,
          this.menuButtonRef().nativeElement
        )
        .then(() => {
          if (this.shareOpen()) this.toggleShare();
        });
    }
  }

  public open() {
    this.shareOpen.set(true);
    this.winService
      .onClickedOutside(
        this.menuRef().nativeElement,
        this.menuButtonRef().nativeElement
      )
      .then(() => {
        if (this.shareOpen()) this.toggleShare();
      });
  }

  protected confirmSMS(payload: {
    channel: GenesysInvitationChannelEnum;
    queue?: IQueue;
    workflow?: string;
  }) {
    this.isPhoneNumberValidation = true;
    this.setFlowData(payload, "sms");
  }

  protected confirmEmail(payload: {
    channel: GenesysInvitationChannelEnum;
    queue?: IQueue;
    workflow?: string;
  }) {
    this.isEmailValidation = true;
    this.setFlowData(payload, "email");
  }

  private setFlowData(
    payload: {
      channel: GenesysInvitationChannelEnum;
      queue?: IQueue;
      workflow?: string;
    },
    type: "email" | "sms"
  ) {
    switch (type) {
      case "sms":
        this.smsQueue = payload.queue;
        break;
      case "email":
        this.emailQueue = payload.queue;
        break;
    }
    this.channel = payload.channel;
    if (payload.workflow) this.workflow = payload.workflow;
  }

  protected callbackRequest() {
    this.isCallbackRequest = true;
    this.shareOpen.set(false);
  }

  protected closeCallbackPanel() {
    this.isCallbackRequest = false;
  }

  protected openChange(value: boolean) {
    this.shareOpen.set(value);
  }

  protected completed() {
    this.renewCustomerLink();
  }

  protected async renewCustomerLink() {
    try {
      const ticketRequest = this.invitationService.prepareTicketRequest(
        this.config.agentParamEnabled(AgentParam.SINGLE_USE_TICKET_ENABLED)
          ? TicketTypeEnum.SingleUseTicket
          : TicketTypeEnum.MultiUseTicket,
        this.config.agentParam(AgentParam.TICKET_LENGTH) ?? 6,
        this.interaction().getRoom(),
        this.interaction().getCustomerId(),
        this.interaction().getId(),
        this.interaction().getCustomerName()
      );
      this.customerTicket.set(
        await this.ticketService.createTicket(ticketRequest)
      );
      this.customerLink.set(
        this.ticketService.getTicketWithDomain(this.customerTicket())
      );
    } catch (ex) {
      this.errorHandler.handleError(ex);
      this.notification.error("Oups!", {
        body: "Could not create shareable link"
      });
    }
  }

  protected copyCustomerLink() {
    if (this.customerLink()) {
      copyTextToClipboard(
        this.interaction().getCustomerLink(this.customerLink())
      );
      // this.notification.show("auvious", {
      //   body: this.translate.instant(
      //     "The video link has been copied to your clipboard"
      //   ),
      // });
      this.notifyCopied();
      this.shareOpen.set(false);
    }
    this.completed();
  }

  protected copyAgentLink() {
    if (this.agentLink()) {
      const link = this.enrichAgentLink(this.agentLink());
      copyTextToClipboard(link);
      // this.notification.show("auvious", {
      //   body: this.translate.instant(
      //     "The agent link has been copied to your clipboard"
      //   ),
      // });
      this.notifyCopied();
      this.shareOpen.set(false);
    } else {
      this.notification.error("auvious", {
        body: "Could not generate agent link"
      });
    }
  }

  private enrichAgentLink(agentLink: string): string {
    let link = this.interaction().getCustomerLink(agentLink);
    if (this.interaction().getOrigin() === ConversationOriginEnum.WIDGET) {
      const l = new URL(link);
      l.searchParams.set(
        PARAM_CONVERSATION_ORIGIN,
        ConversationOriginEnum.WIDGET
      );
      link = l.toString();
    }
    return link;
  }

  protected showQR() {
    this.qrService.showQR(this.enrichAgentLink(this.agentLink()));
    this.shareOpen.set(false);
  }

  public notifyCopied() {
    this.isCopied.set(true);
    setTimeout(() => this.isCopied.set(false), 1400);
  }

  protected get isShareToggleActive() {
    return computed(
      () =>
        this.shareOpen() ||
        this.isCallbackRequest ||
        this.isPhoneNumberValidation ||
        this.isEmailValidation
    );
  }

  protected get isDestinationMobileOffice() {
    return (
      this.interaction().getDestination() ===
      ConversationDestinationEnum.MOBILE_OFFICE
    );
  }

  protected get isOriginWidget() {
    return this.interaction().getOrigin() === ConversationOriginEnum.WIDGET;
  }

  protected get isOriginCallback() {
    return this.interaction().getType() === ConversationTypeEnum.callback;
  }

  protected get isOriginAppointment() {
    return (
      this.interaction().getOrigin() === ConversationOriginEnum.APPOINTMENT
    );
  }

  protected get isSendChatWidgetAvailable() {
    return this.isOriginWidgetWithoutVideo();
  }

  protected get isEmailAvailable() {
    return !this.isDestinationMobileOffice;
  }

  protected get isSMSAvailable() {
    return !this.isDestinationMobileOffice;
  }

  protected get isQRCustomerAvailable() {
    return (
      this.mediaRules.isQRInvitationAvailable &&
      this.interaction().getType() !== ConversationTypeEnum.voiceCall
    );
  }

  protected get isQRAgentAvailable() {
    return this.mediaRules.isQRInvitationAvailable;
  }

  protected get isCallbackAvailable() {
    return (
      this.config.agentParamEnabled(AgentParam.SCHEDULE_ENABLED) &&
      this.interaction().supportsScheduledCalls() &&
      this.config.agentParam(AgentParam.SCHEDULE_CHANNEL) ===
        ScheduleTypeEnum.genesysCallback
    );
  }

  protected get isSendChatAvailable() {
    return (
      this.interaction().supportsMessages() &&
      !this.isOriginWidget &&
      !this.isOriginCallback
    );
  }

  protected get isAgentLinkAvailable() {
    return (
      this.isAgent &&
      !this.isTestApplication &&
      this.mediaRules.isAgentInvitationAvailable
    );
  }

  protected get isCustomerLinkAvailable() {
    return this.mediaRules.isCustomerInvitationAvailalbe;
  }
}
